import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Tag, Badge, Statistic, Space, Input, Collapse, Tooltip, Button, message, Modal, List, Empty, Pagination } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, DesktopOutlined, ReloadOutlined, SearchOutlined, WifiOutlined, CloudServerOutlined, CloudOutlined, AlertOutlined, CheckOutlined, FileTextOutlined } from '@ant-design/icons';
import { targetsAPI, agentsAPI, influxTargetsAPI, alertsAPI } from '../services/api';
import socketService from '../services/socket';
import { useNavigate } from 'react-router-dom';
import {
  getInfluxMonitorStatus,
  isActiveInfluxTarget,
  influxStatusTagColor,
  influxStatusLabel,
  normalizeInfluxStatus,
  isInfluxUpStatus,
} from '../utils/influxStatus';
import { parseApiData, parseAlertLogsResponse } from '../utils/apiResponse';
import { mapServerAlert, clearInfluxAckOnStatusChange } from '../utils/alertDedupe';
import { buildPhase1AcknowledgedIssueList, patchHardwareHealthMap } from '../utils/phase1ActiveIssues';
import { clearPhase1OnTargetRecovery, clearPhase1OnInfluxRecovery, clearPhase1OnHardwareRecovery, AGENT_ALERT_TYPES_NO_ACK } from '../utils/incidentAck';
import {
  getAlertStatusIcon,
  resolveAlertPresentation,
  normalizeTimeoutDownMessage,
  normalizeInfluxAnomalyMessage,
} from '../utils/alertDisplay';

/** API uses last_status; socket may send status — normalize for counts & table */
const getTargetStatus = (target) => {
  const raw = target?.last_status ?? target?.status;
  return raw ? String(raw).toUpperCase() : null;
};

const isActiveTarget = (target) =>
  target?.is_active !== false && target?.is_active !== 0 && target?.is_active !== '0';

const SYSTEM_LOGS_ACK = new Set(['system-replace', 'system-recovery']);

function shouldShowLogsAck(row) {
  if (AGENT_ALERT_TYPES_NO_ACK.has(row?.type)) return false;
  const ackBy = row?.acknowledgedBy ?? row?.acknowledged_by;
  if (ackBy && SYSTEM_LOGS_ACK.has(ackBy)) return false;
  return Boolean(row?.isAcknowledged || row?.is_acknowledged);
}

const ACK_PAGE_SIZE_DEFAULT = 10;

const MonitoringOverview = () => {
  const navigate = useNavigate();
  const [targets, setTargets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentItems, setAgentItems] = useState({});
  const [influxTargets, setInfluxTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null); // 'UP' | 'DOWN' | 'TIMEOUT' | null
  const [influxStatusFilter, setInfluxStatusFilter] = useState(null); // 'UP' | 'ANOMALY' | 'DOWN' | null
  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsAlertCount, setLogsAlertCount] = useState(0);
  const [logsAlerts, setLogsAlerts] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(ACK_PAGE_SIZE_DEFAULT);
  const [logsSearch, setLogsSearch] = useState('');
  const [logsSearchInput, setLogsSearchInput] = useState('');
  const [activeIssueModalVisible, setActiveIssueModalVisible] = useState(false);
  const [hardwareHealthMap, setHardwareHealthMap] = useState({});
  const [ackListVersion, setAckListVersion] = useState(0);

  // Navigate to monitor page with target selected
  const goToMonitor = (targetId) => {
    navigate(`/?target=${targetId}`);
  };

  const goToInfluxMenu = () => {
    navigate('/influxdb');
  };

  const toggleTargetStatusFilter = (status) => {
    setInfluxStatusFilter(null);
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const toggleInfluxStatusFilter = (filter) => {
    setStatusFilter(null);
    setInfluxStatusFilter((prev) => (prev === filter ? null : filter));
  };

  const clearAllOverviewFilters = () => {
    setStatusFilter(null);
    setInfluxStatusFilter(null);
  };

  const acknowledgedIssueList = useMemo(
    () => buildPhase1AcknowledgedIssueList(targets, influxTargets, hardwareHealthMap, agents),
    [targets, influxTargets, hardwareHealthMap, agents, ackListVersion]
  );

  const loadLogsAlerts = useCallback(async (page = 1, pageSize = logsPageSize, search = logsSearch) => {
    setLogsLoading(true);
    try {
      const safePage = Math.max(1, page);
      const offset = (safePage - 1) * pageSize;
      const res = await alertsAPI.getLogs(pageSize, offset, search);
      const { rows, total } = parseAlertLogsResponse(res);
      setLogsAlerts(rows);
      setLogsAlertCount(total);
      setLogsPage(safePage);
      setLogsPageSize(pageSize);
    } catch (err) {
      console.error('Failed to load logs alerts:', err);
      message.error('Failed to load logs alert');
      setLogsAlerts([]);
    } finally {
      setLogsLoading(false);
    }
  }, [logsPageSize, logsSearch]);

  const fetchLogsAlertCount = useCallback(async (search = logsSearch) => {
    try {
      const res = await alertsAPI.getLogs(1, 0, search);
      const { total } = parseAlertLogsResponse(res);
      setLogsAlertCount(total);
    } catch {
      setLogsAlertCount(0);
    }
  }, [logsSearch]);

  useEffect(() => {
    const bump = () => setAckListVersion((v) => v + 1);
    window.addEventListener('pingpulse-phase1-ack', bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener('pingpulse-phase1-ack', bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  const applyLogsSearch = useCallback(() => {
    setLogsSearch(logsSearchInput.trim());
    loadLogsAlerts(1, logsPageSize, logsSearchInput.trim());
  }, [loadLogsAlerts, logsPageSize, logsSearchInput]);

  const handleLogsPageChange = useCallback((page, pageSize) => {
    loadLogsAlerts(page, pageSize, logsSearch);
  }, [loadLogsAlerts, logsSearch]);

  const openLogsAlertModal = useCallback(() => {
    setLogsModalVisible(true);
    loadLogsAlerts(1, logsPageSize, logsSearch);
  }, [loadLogsAlerts, logsPageSize, logsSearch]);

  const formatLogAlertDescription = (row) => {
    const mapped = mapServerAlert(row);
    if (mapped.type === 'influxdb') {
      return normalizeInfluxAnomalyMessage(mapped.description, mapped.data?.anomalyDetails);
    }
    return normalizeTimeoutDownMessage(mapped.description);
  };

  const goToIssueTarget = (row) => {
    if (row.kind === 'influx') {
      goToInfluxMenu();
      return;
    }
    if (row.kind === 'hardware') {
      navigate(`/agents/${row.targetId}`);
      return;
    }
    if (row.targetId) {
      goToMonitor(row.targetId);
    }
  };

  const goToActiveIssueTarget = (row) => {
    setActiveIssueModalVisible(false);
    goToIssueTarget(row);
  };

  const goToLogsTarget = (row) => {
    setLogsModalVisible(false);
    if (row.type === 'influxdb') {
      goToInfluxMenu();
      return;
    }
    if (['monitored-item', 'resource', 'event-anomaly'].includes(row.type) && row.targetId) {
      navigate(`/agents/${row.targetId}`);
      return;
    }
    if (row.targetId) {
      goToMonitor(row.targetId);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const errors = [];
    try {
      const [targetResult, agentResult, influxResult] = await Promise.allSettled([
        targetsAPI.getTargets(),
        agentsAPI.getAll(),
        influxTargetsAPI.getAll(),
      ]);

      if (targetResult.status === 'fulfilled') {
        const rows = parseApiData(targetResult.value, []);
        setTargets(rows);
        if (targetResult.value?.success === false) {
          errors.push('targets');
        }
      } else {
        errors.push('targets');
        console.error('Overview targets fetch failed:', targetResult.reason);
      }

      if (agentResult.status === 'fulfilled') {
        const body = agentResult.value;
        const agentList = body?.data?.agents || body?.agents || parseApiData(body, []);
        setAgents(Array.isArray(agentList) ? agentList : []);
        const itemsMap = {};
        await Promise.all((Array.isArray(agentList) ? agentList : []).map(async (a) => {
          try {
            const itemRes = await agentsAPI.getMonitoredItems(a.agent_id);
            if (itemRes?.success !== false) {
              itemsMap[a.agent_id] = parseApiData(itemRes, []);
            }
          } catch (e) { /* ignore */ }
        }));
        setAgentItems(itemsMap);
        if (body?.success === false) {
          errors.push('agents');
        }
      } else {
        errors.push('agents');
        console.error('Overview agents fetch failed:', agentResult.reason);
      }

      if (influxResult.status === 'fulfilled') {
        const rows = parseApiData(influxResult.value, []);
        setInfluxTargets(rows);
        if (influxResult.value?.success === false) {
          errors.push('influx');
        } else if (rows.length === 0) {
          try {
            const alertRes = await alertsAPI.getActive(20);
            const alerts = parseApiData(alertRes, []);
            const hasInfluxAlerts = alerts.some((a) => a.type === 'influxdb');
            if (hasInfluxAlerts) {
              message.warning(
                'Alert InfluxDB ada di server, tapi GET /influx-targets mengembalikan 0 baris. '
                + 'Backend mungkin pakai DATABASE_URL berbeda — cek .env backend.'
              );
            }
          } catch {
            /* ignore */
          }
        }
      } else {
        errors.push('influx');
        console.error('Overview influx fetch failed:', influxResult.reason);
      }

      if (errors.length > 0) {
        message.warning(`Gagal memuat sebagian data: ${errors.join(', ')}. Cek backend/API URL lalu Refresh.`);
      }
      await fetchLogsAlertCount();
    } catch (e) {
      console.error('Error fetching overview data:', e);
      message.error('Gagal memuat Overview. Pastikan backend jalan dan REACT_APP_API_URL benar.');
    } finally {
      setLoading(false);
    }
  }, [fetchLogsAlertCount]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleStatusUpdate = (data) => {
      setTargets(prev => prev.map(t =>
        t.id === data.targetId
          ? {
            ...t,
            last_status: data.status,
            status: data.status,
            last_latency: data.latency ?? t.last_latency,
            latency: data.latency,
            last_check: data.timestamp,
            consecutive_failures: data.consecutiveFailures ?? t.consecutive_failures,
            consecutive_timeout_threshold: data.consecutiveThreshold ?? t.consecutive_timeout_threshold,
          }
          : t
      ));
      const status = String(data.status || '').toUpperCase();
      if (status === 'UP' || status === 'OK') {
        clearPhase1OnTargetRecovery(data.targetId);
      }
    };

    const handleAgentStatus = (data) => {
      setAgents(prev => prev.map(a =>
        a.agent_id === data.agent_id ? { ...a, online: data.online, last_seen: data.timestamp } : a
      ));
    };

    const patchInfluxTarget = (influxTargetId, patch) => {
      const id = Number(influxTargetId);
      setInfluxTargets((prev) => prev.map((t) => {
        if (t.id !== id && t.id !== influxTargetId) return t;
        const status = patch.status ?? patch.last_status ?? t.last_status;
        const normalized = normalizeInfluxStatus(status) || status;
        const isRecovery = isInfluxUpStatus(normalized);
        return {
          ...t,
          ...patch,
          last_status: normalized,
          status: normalized,
          last_error: isRecovery ? null : (patch.last_error ?? patch.errorMessage ?? t.last_error),
          last_check: patch.timestamp || patch.last_check || t.last_check,
        };
      }));
    };

    const handleInfluxStatusUpdate = (data) => {
      if (!data?.influxTargetId) return;
      clearInfluxAckOnStatusChange(data);
      if (isInfluxUpStatus(data.status)) {
        clearPhase1OnInfluxRecovery(data.influxTargetId);
      }
      patchInfluxTarget(data.influxTargetId, {
        last_status: data.status,
        status: data.status,
        last_error: data.errorMessage || data.error || null,
        timestamp: data.timestamp,
      });
    };

    const handleHardwareUpdate = (data) => {
      setHardwareHealthMap((prev) => patchHardwareHealthMap(prev, data));
      const issues = data?.summary?.issues;
      const overall = data?.summary?.overall;
      const healthy = (
        (Array.isArray(issues) && issues.length === 0)
        || overall === 'Healthy'
        || overall === 'OK'
      );
      if (healthy) {
        clearPhase1OnHardwareRecovery(data.agent_id ?? data.agentId);
      }
    };

    const bumpLogsCount = () => {
      fetchLogsAlertCount();
    };

    const handleInfluxAlert = (data) => {
      if (!data?.targetId) return;
      patchInfluxTarget(data.targetId, {
        last_status: data.status || 'ANOMALY',
        status: data.status || 'ANOMALY',
        last_error: data.error || null,
        timestamp: data.timestamp,
      });
      bumpLogsCount();
    };

    socket.on('status-update', handleStatusUpdate);
    socket.on('agent-status-change', handleAgentStatus);
    socket.on('influx-status-update', handleInfluxStatusUpdate);
    socket.on('influxdb-alert', handleInfluxAlert);
    socket.on('target-status-alert', bumpLogsCount);
    socket.on('agent-hardware-update', handleHardwareUpdate);
    socket.on('hardware-health-alert', handleHardwareUpdate);
    socket.emit('subscribe-targets');
    return () => {
      socket.off('status-update', handleStatusUpdate);
      socket.off('agent-status-change', handleAgentStatus);
      socket.off('influx-status-update', handleInfluxStatusUpdate);
      socket.off('influxdb-alert', handleInfluxAlert);
      socket.off('target-status-alert', bumpLogsCount);
      socket.off('agent-hardware-update', handleHardwareUpdate);
      socket.off('hardware-health-alert', handleHardwareUpdate);
    };
  }, [fetchLogsAlertCount]);

  // Group targets by prefix (before first " - ")
  const groupTargets = (targets) => {
    const groups = {};
    const ungrouped = [];
    targets.forEach(t => {
      const dashIdx = t.name.indexOf(' - ');
      if (dashIdx > 0) {
        const group = t.name.substring(0, dashIdx).trim();
        if (!groups[group]) groups[group] = [];
        groups[group].push(t);
      } else {
        ungrouped.push(t);
      }
    });
    if (ungrouped.length > 0) groups['Other'] = ungrouped;
    return groups;
  };

  // Same base set for cards and table: active targets only (ISSUE-005)
  const activeTargets = targets.filter(isActiveTarget);

  const filtered = search
    ? activeTargets.filter(t =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.host.toLowerCase().includes(search.toLowerCase())
    )
    : activeTargets;

  const displayTargets = statusFilter
    ? filtered.filter(t => getTargetStatus(t) === statusFilter)
    : filtered;

  const targetGroups = groupTargets(displayTargets);

  const countByStatus = (status) =>
    filtered.filter(t => getTargetStatus(t) === status).length;

  const upCount = countByStatus('UP');
  const downCount = countByStatus('DOWN');
  const timeoutCount = countByStatus('TIMEOUT');
  const onlineAgents = agents.filter(a => a.online).length;
  const totalItems = Object.values(agentItems).flat().length;
  const runningItems = Object.values(agentItems).flat().filter(i => i.last_status === 'running').length;
  const activeInfluxList = influxTargets.filter(isActiveInfluxTarget);
  const activeInfluxTargets = activeInfluxList.length;
  const influxUpCount = activeInfluxList.filter((t) => getInfluxMonitorStatus(t) === 'UP').length;
  const influxAnomalyCount = activeInfluxList.filter((t) => getInfluxMonitorStatus(t) === 'ANOMALY').length;
  const influxDownCount = activeInfluxList.filter((t) => {
    const s = getInfluxMonitorStatus(t);
    return s === 'DOWN' || s === 'ERROR';
  }).length;

  const displayedInfluxTargets = influxStatusFilter
    ? activeInfluxList.filter((t) => {
      const s = getInfluxMonitorStatus(t);
      if (influxStatusFilter === 'DOWN') return s === 'DOWN' || s === 'ERROR';
      return s === influxStatusFilter;
    })
    : activeInfluxList;

  const showNetworkSection = !influxStatusFilter;
  const showAgentSection = !statusFilter && !influxStatusFilter;
  const showInfluxSection = !statusFilter;
  const hasOverviewFilter = Boolean(statusFilter || influxStatusFilter);

  const statusIcon = (status) => {
    if (status === 'UP') return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} />;
    if (status === 'DOWN') return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 14 }} />;
    return <ClockCircleOutlined style={{ color: '#faad14', fontSize: 14 }} />;
  };

  const collapseItems = Object.entries(targetGroups).map(([group, items]) => {
    const gUp = items.filter(t => getTargetStatus(t) === 'UP').length;
    const gDown = items.filter(t => getTargetStatus(t) === 'DOWN').length;
    const gTimeout = items.filter(t => getTargetStatus(t) === 'TIMEOUT').length;

    return {
      key: group,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: 8 }}>
          <Space>
            <strong>{group}</strong>
            <Tag>{items.length}</Tag>
          </Space>
          <Space size={4}>
            {gUp > 0 && <Tag color="success" style={{ margin: 0 }}>{gUp} UP</Tag>}
            {gDown > 0 && <Tag color="error" style={{ margin: 0 }}>{gDown} DOWN</Tag>}
            {gTimeout > 0 && <Tag color="warning" style={{ margin: 0 }}>{gTimeout} TIMEOUT</Tag>}
          </Space>
        </div>
      ),
      children: (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map(t => {
            const rowStatus = getTargetStatus(t);
            const rowLatency = t.last_latency ?? t.latency;
            return (
            <div
              key={t.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 6,
                background: rowStatus === 'UP' ? '#f6ffed' : rowStatus === 'DOWN' ? '#fff2f0' : '#fffbe6',
                border: `1px solid ${rowStatus === 'UP' ? '#b7eb8f' : rowStatus === 'DOWN' ? '#ffccc7' : '#ffe58f'}`,
                minWidth: 240, maxWidth: 320, width: '100%',
                cursor: 'pointer', transition: 'box-shadow 0.2s'
              }}
              onClick={() => goToMonitor(t.id)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              {statusIcon(rowStatus)}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.name.includes(' - ') ? t.name.substring(t.name.indexOf(' - ') + 3) : t.name}
                </div>
                <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.host}{t.port ? `:${t.port}` : ''}
                </div>
              </div>
              <Space size={4}>
                <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{t.target_type}</Tag>
                {rowLatency != null && <Tag style={{ margin: 0, fontSize: 10 }}>{rowLatency}ms</Tag>}
              </Space>
            </div>
            );
          })}
        </div>
      )
    };
  });

  const CARD_BODY = { padding: '12px 16px' };
  const CARD_STYLE = { marginBottom: 0 };

  const filterSummaryCardStyle = (active, accent) => ({
    ...CARD_STYLE,
    position: 'relative',
    border: active ? `2px solid ${accent}` : undefined,
    boxShadow: active ? `0 0 0 2px ${accent}33` : undefined,
  });

  const influxFilterLabel = (filter) => (filter === 'UP' ? 'OK' : filter);

  const formatInfluxLastDataNote = (target, monitorStatus) => {
    let note = target.last_data_note || target.last_error || null;
    if (!note && monitorStatus === 'UP') {
      note = 'Latest data OK';
    }
    if (!note) {
      note = 'No data received yet';
    }
    if (monitorStatus === 'ANOMALY' || monitorStatus === 'DOWN' || monitorStatus === 'ERROR') {
      note = normalizeInfluxAnomalyMessage(note);
    }
    return note;
  };

  const influxDataNoteColor = (monitorStatus) => {
    if (monitorStatus === 'ANOMALY') return '#fa8c16';
    if (monitorStatus === 'DOWN' || monitorStatus === 'ERROR') return '#cf1322';
    if (monitorStatus === 'UP') return '#389e0d';
    return '#888';
  };

  const filterActiveDot = (active, accent) => active ? (
    <div
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: accent,
      }}
    />
  ) : null;

  return (
    <div style={{ padding: 24 }}>
      {hasOverviewFilter && (
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={clearAllOverviewFilters}>
            Tampilkan semua
          </Button>
        </div>
      )}
      {/* Summary */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" styles={{ body: CARD_BODY }} style={CARD_STYLE}>
            <Statistic title="Targets" value={activeTargets.length} prefix={<WifiOutlined />} valueStyle={{ fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={filterSummaryCardStyle(statusFilter === 'UP', '#52c41a')}
            hoverable
            onClick={() => toggleTargetStatusFilter('UP')}
          >
            <Statistic title="UP" value={upCount} valueStyle={{ color: '#52c41a', fontSize: 24 }} prefix={<CheckCircleOutlined />} />
            {filterActiveDot(statusFilter === 'UP', '#52c41a')}
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={filterSummaryCardStyle(statusFilter === 'DOWN', '#ff4d4f')}
            hoverable
            onClick={() => toggleTargetStatusFilter('DOWN')}
          >
            <Statistic title="DOWN" value={downCount} valueStyle={{ color: downCount > 0 ? '#ff4d4f' : '#d9d9d9', fontSize: 24 }} prefix={<CloseCircleOutlined />} />
            {filterActiveDot(statusFilter === 'DOWN', '#ff4d4f')}
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={filterSummaryCardStyle(statusFilter === 'TIMEOUT', '#faad14')}
            hoverable
            onClick={() => toggleTargetStatusFilter('TIMEOUT')}
          >
            <Statistic title="TIMEOUT" value={timeoutCount} valueStyle={{ color: timeoutCount > 0 ? '#faad14' : '#d9d9d9', fontSize: 24 }} prefix={<ClockCircleOutlined />} />
            {filterActiveDot(statusFilter === 'TIMEOUT', '#faad14')}
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" styles={{ body: CARD_BODY }} style={CARD_STYLE}>
            <Statistic title="Agents" value={`${onlineAgents}/${agents.length}`} valueStyle={{ color: onlineAgents > 0 ? '#52c41a' : '#d9d9d9', fontSize: 24 }} prefix={<CloudServerOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" styles={{ body: CARD_BODY }} style={CARD_STYLE}>
            <Statistic title="Services" value={`${runningItems}/${totalItems}`} valueStyle={{ color: runningItems > 0 ? '#52c41a' : '#d9d9d9', fontSize: 24 }} prefix={<DesktopOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={CARD_STYLE}
            hoverable
            onClick={goToInfluxMenu}
          >
            <Statistic
              title="InfluxDB"
              value={influxTargets.length}
              suffix={<span style={{ fontSize: 11, color: '#888' }}>act:{activeInfluxTargets}</span>}
              valueStyle={{ fontSize: 22 }}
              prefix={<CloudOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={filterSummaryCardStyle(influxStatusFilter === 'UP', '#52c41a')}
            hoverable
            onClick={() => toggleInfluxStatusFilter('UP')}
          >
            <Statistic
              title="Influx OK"
              value={influxUpCount}
              valueStyle={{ color: influxUpCount > 0 ? '#52c41a' : '#d9d9d9', fontSize: 22 }}
              prefix={<CheckCircleOutlined />}
            />
            {filterActiveDot(influxStatusFilter === 'UP', '#52c41a')}
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={filterSummaryCardStyle(influxStatusFilter === 'ANOMALY', '#fa8c16')}
            hoverable
            onClick={() => toggleInfluxStatusFilter('ANOMALY')}
          >
            <Statistic
              title="Influx ANOMALY"
              value={influxAnomalyCount}
              valueStyle={{ color: influxAnomalyCount > 0 ? '#fa8c16' : '#d9d9d9', fontSize: 22 }}
              prefix={<AlertOutlined />}
            />
            {filterActiveDot(influxStatusFilter === 'ANOMALY', '#fa8c16')}
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={filterSummaryCardStyle(influxStatusFilter === 'DOWN', '#ff4d4f')}
            hoverable
            onClick={() => toggleInfluxStatusFilter('DOWN')}
          >
            <Statistic
              title="Influx DOWN"
              value={influxDownCount}
              valueStyle={{ color: influxDownCount > 0 ? '#ff4d4f' : '#d9d9d9', fontSize: 22 }}
              prefix={<CloseCircleOutlined />}
            />
            {filterActiveDot(influxStatusFilter === 'DOWN', '#ff4d4f')}
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={CARD_STYLE}
            hoverable
            onClick={openLogsAlertModal}
          >
            <Statistic
              title="Logs Alert"
              value={logsAlertCount}
              valueStyle={{ color: logsAlertCount > 0 ? '#1677ff' : '#d9d9d9', fontSize: 22 }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            styles={{ body: CARD_BODY }}
            style={CARD_STYLE}
            hoverable
            onClick={() => {
              setAckListVersion((v) => v + 1);
              setActiveIssueModalVisible(true);
            }}
          >
            <Statistic
              title="Acknowledge"
              value={acknowledgedIssueList.length}
              valueStyle={{ color: acknowledgedIssueList.length > 0 ? '#722ed1' : '#d9d9d9', fontSize: 22 }}
              prefix={<CheckOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Target Groups */}
      {showNetworkSection && (
      <Card
        title={<><WifiOutlined /> Network & Service Monitoring</>}
        headStyle={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}
        size="small"
        extra={
          <Space>
            {statusFilter && (
              <Button
                size="small"
                onClick={(e) => { e.stopPropagation(); setStatusFilter(null); }}
              >
                Clear Filter ({statusFilter})
              </Button>
            )}
            <Input
              placeholder="Search targets..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 180 }}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>Refresh</Button>
          </Space>
        }
        style={{ marginBottom: 20 }}
      >
        {activeTargets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {targets.length === 0 ? 'No targets configured' : 'No active targets'}
          </div>
        ) : displayTargets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            Tidak ada target dengan status {statusFilter}
          </div>
        ) : (
          <Collapse
            defaultActiveKey={Object.keys(targetGroups)}
            items={collapseItems}
            size="small"
            ghost
          />
        )}
      </Card>
      )}

      {/* Agent Groups */}
      {showAgentSection && (
      <Card
        title={<><CloudServerOutlined /> Agent Monitoring</>}
        headStyle={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}
        size="small"
        extra={<Button size="small" onClick={() => navigate('/agents')}>View All</Button>}
        style={{ marginBottom: 20 }}
      >
        {agents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No agents configured</div>
        ) : (
          <Row gutter={[12, 12]}>
            {agents.map(agent => {
              const items = agentItems[agent.agent_id] || [];
              return (
                <Col xs={24} sm={12} md={8} key={agent.agent_id}>
                  <Card
                    size="small"
                    hoverable
                    onClick={() => navigate(`/agents/${agent.agent_id}`)}
                    style={{ borderLeft: `3px solid ${agent.online ? '#52c41a' : '#d9d9d9'}`, marginBottom: 0 }}
                    styles={{ body: { padding: 16 } }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Space>
                        <Badge status={agent.online ? 'success' : 'default'} />
                        <strong>{agent.agent_name}</strong>
                      </Space>
                      <Space size={4}>
                        <Tag color={agent.platform === 'windows' ? 'blue' : 'green'} style={{ margin: 0 }}>
                          {agent.platform?.toUpperCase()}
                        </Tag>
                        <Tag color={agent.online ? 'success' : 'default'} style={{ margin: 0 }}>
                          {agent.online ? 'Online' : 'Offline'}
                        </Tag>
                      </Space>
                    </div>

                    {agent.agent_description && (
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>{agent.agent_description}</div>
                    )}

                    {items.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {items.map(item => (
                          <Tooltip key={item.id} title={`${item.item_type}: ${item.item_name} - ${item.last_status || 'unknown'}`}>
                            <Tag
                              style={{ margin: 0, fontSize: 11 }}
                              color={item.last_status === 'running' ? 'success' : item.last_status === 'stopped' ? 'error' : item.last_status === 'errored' ? 'volcano' : 'default'}
                            >
                              <span style={{ marginRight: 4 }}>
                                {item.item_type === 'pm2' ? '⚡' : item.item_type === 'process' ? '⚙' : '🔧'}
                              </span>
                              {item.item_name}
                            </Tag>
                          </Tooltip>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: '#bbb' }}>No monitored items</div>
                    )}

                    <div style={{ fontSize: 10, color: '#aaa', marginTop: 8 }}>
                      Last seen: {agent.last_seen ? new Date(agent.last_seen).toLocaleString('id-ID') : '-'}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>
      )}

      {/* InfluxDB Targets */}
      {showInfluxSection && (
      <Card
        title={<><CloudOutlined /> InfluxDB Monitoring</>}
        headStyle={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}
        size="small"
        extra={
          <Space>
            {influxStatusFilter && (
              <Button size="small" onClick={() => setInfluxStatusFilter(null)}>
                Clear Filter ({influxFilterLabel(influxStatusFilter)})
              </Button>
            )}
            <Button type="primary" size="small" onClick={goToInfluxMenu}>View Details</Button>
          </Space>
        }
      >
        {influxTargets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>No InfluxDB targets configured</div>
        ) : displayedInfluxTargets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            Tidak ada target Influx dengan status {influxFilterLabel(influxStatusFilter)}
          </div>
        ) : (
          <Row gutter={[12, 12]}>
            {displayedInfluxTargets.map(target => {
              const hasRecentCheck = target.last_check && (Date.now() - new Date(target.last_check).getTime() < 3600000);
              const monitorStatus = getInfluxMonitorStatus(target);
              const statusColor = influxStatusTagColor(monitorStatus);
              const borderColor = !target.is_active
                ? '#d9d9d9'
                : monitorStatus === 'ANOMALY'
                  ? '#fa8c16'
                  : monitorStatus === 'DOWN' || monitorStatus === 'ERROR'
                    ? '#ff4d4f'
                    : monitorStatus === 'UP'
                      ? '#52c41a'
                      : '#1890ff';
              return (
                <Col xs={24} sm={12} md={8} key={target.id}>
                  <Card
                    size="small"
                    hoverable
                    onClick={goToInfluxMenu}
                    style={{ borderLeft: `3px solid ${borderColor}`, marginBottom: 0 }}
                    styles={{ body: { padding: 16 } }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, lineHeight: 1.4 }}>
                        {target.name}
                      </div>
                      <Space size={4} wrap style={{ justifyContent: 'center', marginBottom: 10 }}>
                        <Tag color={statusColor} style={{ margin: 0 }}>
                          {influxStatusLabel(monitorStatus)}
                        </Tag>
                        <Tag color={target.is_active ? 'blue' : 'default'} style={{ margin: 0 }}>
                          {target.is_active ? 'Active' : 'Inactive'}
                        </Tag>
                        {target.last_check && (
                          <Tag color={hasRecentCheck ? 'success' : 'warning'} style={{ margin: 0 }}>
                            {hasRecentCheck ? 'Checked' : 'Stale'}
                          </Tag>
                        )}
                      </Space>

                      {(() => {
                        const dataNote = formatInfluxLastDataNote(target, monitorStatus);
                        return (
                          <div
                            style={{
                              fontSize: 11,
                              color: influxDataNoteColor(monitorStatus),
                              marginBottom: 8,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'center',
                            }}
                            title={dataNote}
                          >
                            {dataNote}
                          </div>
                        );
                      })()}

                      <Space size={4} wrap style={{ justifyContent: 'center', marginBottom: 6 }}>
                        <Tag style={{ margin: 0, fontSize: 11 }}>Interval: {target.check_interval}s</Tag>
                        {target.anomaly_config?.method && (
                          <Tag color="volcano" style={{ margin: 0, fontSize: 11 }}>
                            <AlertOutlined /> {target.anomaly_config.method}
                          </Tag>
                        )}
                      </Space>

                      <div style={{ fontSize: 10, color: '#aaa', marginTop: 8 }}>
                        Last check: {target.last_check ? new Date(target.last_check).toLocaleString('id-ID') : '-'}
                      </div>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>
      )}

      <Modal
        title={(
          <Space>
            <FileTextOutlined />
            <span>Logs Alert ({logsAlertCount})</span>
          </Space>
        )}
        open={logsModalVisible}
        onCancel={() => {
          setLogsModalVisible(false);
          setLogsPage(1);
        }}
        footer={[
          <Button key="refresh" onClick={() => loadLogsAlerts(logsPage, logsPageSize, logsSearch)} loading={logsLoading}>
            Refresh
          </Button>,
          <Button key="close" type="primary" onClick={() => setLogsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={680}
      >
        <Space style={{ marginBottom: 16, width: '100%' }} wrap>
          <Input
            placeholder="Search by title..."
            prefix={<SearchOutlined />}
            value={logsSearchInput}
            onChange={(e) => setLogsSearchInput(e.target.value)}
            onPressEnter={applyLogsSearch}
            style={{ width: 260 }}
            allowClear
          />
          <Button type="primary" onClick={applyLogsSearch} loading={logsLoading}>Search</Button>
        </Space>
        {logsLoading ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>Loading...</div>
        ) : logsAlerts.length === 0 ? (
          <Empty description="No alert logs" />
        ) : (
          <>
            <List
              dataSource={logsAlerts}
              renderItem={(row) => {
                const mapped = mapServerAlert(row);
                const { status, isRecovery, title } = resolveAlertPresentation(mapped);
                const severityColors = {
                  CRITICAL: '#ff4d4f',
                  HIGH: '#fa8c16',
                  MEDIUM: '#722ed1',
                  LOW: '#52c41a',
                };
                const color = severityColors[mapped.severity] || '#52c41a';
                const createdAt = row.createdAt || row.created_at;
                const ackAt = row.acknowledgedAt || row.acknowledged_at;
                const ackBy = row.acknowledgedBy || row.acknowledged_by;
                const showAck = shouldShowLogsAck(row);
                return (
                  <List.Item
                    key={row.id}
                    actions={row.targetId ? [
                      <Button key="view" type="link" size="small" onClick={() => goToLogsTarget(row)}>
                        View
                      </Button>,
                    ] : []}
                  >
                    <List.Item.Meta
                      avatar={getAlertStatusIcon(status, isRecovery)}
                      title={(
                        <Space size={6} wrap>
                          <span style={{ fontWeight: 500 }}>{title}</span>
                          <Tag color={color} style={{ fontSize: 10, margin: 0 }}>{mapped.severity}</Tag>
                          {(showAck) && (
                            <Tag style={{ fontSize: 10, margin: 0 }}>Ack</Tag>
                          )}
                        </Space>
                      )}
                      description={(
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <span style={{ whiteSpace: 'pre-line' }}>{formatLogAlertDescription(row)}</span>
                          <span style={{ fontSize: 11, color: '#999' }}>
                            {createdAt ? new Date(createdAt).toLocaleString('id-ID') : '-'}
                            {showAck && ackBy ? ` · Ack: ${ackBy}${ackAt ? ` (${new Date(ackAt).toLocaleString('id-ID')})` : ''}` : ''}
                          </span>
                        </Space>
                      )}
                    />
                  </List.Item>
                );
              }}
            />
            <Pagination
              current={logsPage}
              pageSize={logsPageSize}
              total={logsAlertCount}
              onChange={handleLogsPageChange}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
              style={{ marginTop: 16, textAlign: 'right' }}
              disabled={logsLoading}
            />
          </>
        )}
      </Modal>

      <Modal
        title={(
          <Space>
            <CheckOutlined />
            <span>Acknowledge ({acknowledgedIssueList.length})</span>
          </Space>
        )}
        open={activeIssueModalVisible}
        onCancel={() => setActiveIssueModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setActiveIssueModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={640}
      >
        {acknowledgedIssueList.length === 0 ? (
          <Empty description="No acknowledged issues — press Acknowledge on an alert first" />
        ) : (
          <List
            dataSource={acknowledgedIssueList}
            renderItem={(row) => (
              <List.Item
                key={row.key}
                actions={[
                  <Button key="view" type="link" size="small" onClick={() => goToActiveIssueTarget(row)}>
                    View
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={getAlertStatusIcon(row.status, false)}
                  title={(
                    <Space size={6} wrap>
                      <span style={{ fontWeight: 500 }}>{row.name}</span>
                      <Tag color={
                        row.kind === 'influx' ? influxStatusTagColor(row.status)
                          : row.status === 'DOWN' || row.status === 'ERROR' ? 'red'
                            : row.kind === 'hardware' ? 'orange' : 'default'
                      }
                      style={{ fontSize: 10, margin: 0 }}
                      >
                        {row.kind === 'influx' ? influxStatusLabel(row.status) : row.status}
                      </Tag>
                      <Tag style={{ fontSize: 10, margin: 0 }}>{row.kind}</Tag>
                    </Space>
                  )}
                  description={(
                    <span style={{ fontSize: 11, color: '#999' }}>
                      Last check: {row.lastCheck ? new Date(row.lastCheck).toLocaleString('id-ID') : '-'}
                    </span>
                  )}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
};

export default MonitoringOverview;
